import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { lockBodyScroll, openLayerCount } from '../lib/scroll-lock.js';
import { Button } from './Button.js';
const sizeClass = {
    sm: 'max-w-[var(--size-modal-sm)]',
    md: 'max-w-[var(--size-modal-md)]',
    lg: 'max-w-[var(--size-modal-lg)]',
    /** Chizma va jadval uchun — matn o'qiladigan oyna uchun EMAS. */
    xl: 'max-w-[var(--size-modal-xl)]',
};
const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
const focusableIn = (root) => Array.from(root.querySelectorAll(FOCUSABLE)).filter((el) => el.getAttribute('aria-hidden') !== 'true');
/**
 * Ochilganda fokus oladigan element.
 *
 * Yopish tugmasi HISOBGA OLINMAYDI. U tartib bo'yicha birinchi bo'lib
 * turadi (sarlavha tanadan oldin keladi), ya'ni oddiy "birinchi
 * fokuslanadigan element" qoidasi odamni to'g'ri maydonga emas, chiqish
 * yo'liga olib borardi: forma ochiladi-yu, kursor "X" da turadi va
 * birinchi Enter oynani yopadi.
 */
const initialFocusIn = (root) => {
    const items = focusableIn(root).filter((el) => el.dataset.dialogClose !== 'true');
    return items[0] ?? null;
};
/**
 * Modal oyna. Fokus panel ichida qulflanadi, `Esc` va fon bosilishi yopadi,
 * yopilgach fokus avval turgan elementga qaytadi — klaviatura bilan
 * ishlayotgan odam joyini yo'qotmaydi.
 *
 * Yopilish ANIMATSIYA bilan tugaydi: `open` `false` bo'lgach panel darhol
 * yo'q qilinmaydi, chiqish harakati tugagunicha turadi. Shuning uchun
 * ichkarida `rendered` holati bor — u DOM da borligini, `open` esa
 * chaqiruvchining niyatini bildiradi.
 */
export const Dialog = ({ open, onClose, title, description, error, footer, size = 'md', dirty = false, onSubmit, busy = false, headless = false, children, className, bodyClassName, closeLabel = 'Yopish', discardTitle = "Kiritilgan o'zgarishlar saqlanmaydi. Oynani yopamizmi?", discardConfirmLabel = 'Ha, yopilsin', discardCancelLabel = 'Orqaga', }) => {
    const titleId = useId();
    const descriptionId = useId();
    const panelRef = useRef(null);
    const restoreRef = useRef(null);
    /* `open` — chaqiruvchining niyati, `rendered` — DOM dagi haqiqat.
       Ular chiqish animatsiyasi davomida farq qiladi. */
    const [rendered, setRendered] = useState(open);
    const [closing, setClosing] = useState(false);
    const [askDiscard, setAskDiscard] = useState(false);
    /* Ustma-ust tushgan oyna balandroq qatlamda va fon quyuqroq bo'ladi. */
    const [depth, setDepth] = useState(1);
    /*
     * OCHILISH render paytida moslanadi, effektda emas.
     *
     * Effektda qilingan edi va u ishlamadi: `open` `true` bo'lgan birinchi
     * renderda `rendered` hali `false` bo'lib, komponent `null` qaytarardi —
     * ya'ni fokus effekti ishga tushganda PANEL HALI YO'Q edi va fokus hech
     * qayerga tegmasdi. Render paytida holatni moslash React ruxsat bergan
     * naqsh: u darhol qayta chizadi va effektlar allaqachon montaj bo'lgan
     * panelni ko'radi.
     */
    if (open && !rendered)
        setRendered(true);
    if (open && closing)
        setClosing(false);
    /* Yopilish esa KECHIKADI: chiqish harakati tugagunicha panel DOM da qoladi. */
    useEffect(() => {
        if (open || !rendered)
            return;
        setClosing(true);
        const timer = setTimeout(() => {
            setRendered(false);
            setClosing(false);
            setAskDiscard(false);
        }, 160);
        return () => clearTimeout(timer);
    }, [open, rendered]);
    /* Ochilganda: oldingi fokusni eslab qolamiz va BIRINCHI MAYDONGA o'tamiz.
       Yopilganda: fokusni qaytaramiz va sahifa siljishini tiklaymiz. */
    useEffect(() => {
        if (!open)
            return;
        const active = document.activeElement;
        restoreRef.current = active instanceof HTMLElement ? active : null;
        const panel = panelRef.current;
        if (panel)
            (initialFocusIn(panel) ?? panel).focus();
        setDepth(openLayerCount() + 1);
        const unlock = lockBodyScroll();
        return () => {
            unlock();
            restoreRef.current?.focus();
        };
    }, [open]);
    /**
     * Yopishga urinish — bitta joyda.
     *
     * `Esc`, fon bosilishi va "X" tugmasi ayni yo'ldan o'tadi, aks holda
     * ulardan biri tasdiqni chetlab o'tib, terilgan formani yo'qotib
     * yuborardi.
     */
    const requestClose = useCallback(() => {
        if (busy)
            return;
        if (dirty) {
            setAskDiscard(true);
            return;
        }
        onClose();
    }, [busy, dirty, onClose]);
    /* `Esc` hujjat darajasida tinglanadi: fokus panelda bo'lmasa ham ishlaydi. */
    useEffect(() => {
        if (!open)
            return;
        const onKeyDown = (event) => {
            if (event.key !== 'Escape')
                return;
            event.stopPropagation();
            // Tasdiq ochiq bo'lsa `Esc` uni bekor qiladi, oynani emas.
            if (askDiscard) {
                setAskDiscard(false);
                return;
            }
            requestClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, askDiscard, requestClose]);
    /* Fokus qopqoni: `Tab` panel chegarasidan chiqmaydi.
       `Ctrl/Cmd+Enter` esa formani yuboradi. */
    const onPanelKeyDown = useCallback((event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            if (onSubmit && !busy) {
                event.preventDefault();
                onSubmit();
            }
            return;
        }
        if (event.key !== 'Tab')
            return;
        const panel = panelRef.current;
        if (!panel)
            return;
        const items = focusableIn(panel);
        const first = items[0];
        const last = items[items.length - 1];
        if (!first || !last) {
            event.preventDefault();
            panel.focus();
            return;
        }
        const active = document.activeElement;
        if (event.shiftKey && (active === first || active === panel)) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }, [busy, onSubmit]);
    if (!rendered)
        return null;
    return createPortal(_jsx("div", { 
        /*
         * MARKAZLASH `grid` bilan, `flex` bilan emas.
         *
         * `flex justify-center` bolalarni GURUH bo'lib markazlaydi: panel
         * yonida ko'rinmas bo'lsa ham joy egallaydigan ikkinchi element
         * paydo bo'lsa (masalan brauzer kengaytmasi qo'ygan tugun), panel
         * chapga siljib qoladi va o'ng tomonda bo'sh joy qoladi. `grid`
         * da esa har bola O'Z YACHEYKASIDA markazlanadi va bunday siljish
         * mumkin emas.
         *
         * O'lcham `inset-0` dan olinadi, `w-screen` dan emas: `100vw`
         * ga sirg'aluvchi chiziq ham kiradi va u chizma paydo bo'lganda
         * gorizontal siljish yasab qo'yardi.
         *
         * Fonda BLUR yo'q. U ortidagi matnni o'qib bo'lmaydigan loyqaga
         * aylantiradi — modal esa ko'pincha aynan o'sha jadvaldagi qatorga
         * tegishli bo'ladi va odam uni ko'rib turishi kerak.
         *
         * TELEFONDA bo'shliq YO'Q: oyna pastdan chiqadigan varaq bo'lib,
         * ekranning butun kengligini oladi. Yon tomondagi 16px odamga hech
         * narsa bermasdi — ortidagi sahifadan tor chiziq ko'rinardi, oyna
         * esa o'sha ikki chiziq hisobiga toraygan va kontent siqilgan
         * edi. Planshetdan boshlab bo'shliq qaytadi va oyna markazga
         * chiqadi: u yerda ekran kontentdan kengroq va varaq "yopishib"
         * turgani noqulay ko'rinardi.
         */
        style: { zIndex: `calc(var(--z-modal) + ${(depth - 1) * 10})` }, className: cn(closing ? 'motion-backdrop-out' : 'motion-backdrop-in', 'fixed inset-0 grid items-end justify-items-center sm:items-center', 'p-0 sm:p-6', 'bg-[var(--color-overlay)]'), onMouseDown: (event) => {
            if (event.target === event.currentTarget)
                requestClose();
        }, children: _jsxs("div", { ref: panelRef, role: "dialog", "aria-modal": "true", "aria-label": headless ? title : undefined, "aria-labelledby": headless ? undefined : titleId, "aria-describedby": description && !headless ? descriptionId : undefined, "aria-busy": busy || undefined, tabIndex: -1, onKeyDown: onPanelKeyDown, className: cn(closing ? 'motion-modal-out' : 'motion-modal-in', 
            /*
             * Uch qism: sarlavha — tana — pastki panel.
             *
             * Balandlikni AYNAN shu tugun cheklaydi va aylanish faqat
             * tanada bo'ladi. `overflow-hidden` bu yerda majburiy: usiz
             * yumaloq burchaklar ostidan tana chetlari chiqib turardi.
             */
            'relative flex w-full flex-col overflow-hidden', 'max-h-[var(--size-modal-max-h)]', 
            // Chegara YO'Q — chuqurlikni faqat soya beradi. Chegara ham,
            // soya ham bo'lsa, oyna qog'oz emas, ramkaga o'xshab qolardi.
            'bg-surface shadow-[var(--shadow-3)]', 
            /*
             * Telefonda faqat TEPASI yumaloq: varaq ekranning pastki
             * chetidan o'sib chiqadi va pastdagi ikki yumaloq burchak
             * ostida ortidagi sahifa ko'rinib, "osilib qolgan" taassurot
             * berardi. Planshetdan boshlab pastki burchaklar qaytadi.
             *
             * Ikki sinf BIR-BIRINI ustidan yozmaydi (`cn` — oddiy
             * birlashtiruvchi, `tailwind-merge` emas): biri faqat tepa,
             * ikkinchisi faqat past burchaklarni belgilaydi.
             */
            'rounded-t-[var(--radius-lg)] sm:rounded-b-[var(--radius-lg)]', sizeClass[size], className), children: [headless ? null : (
                /* `shrink-0` — SHART. Tana `flex-1` bo'lgani bilan, uzun kontentda
                   flex sarlavhani ham siqishga haqli edi: sarlavha bo'yi qisqarib,
                   matn o'z qutisidan tepadan kesilib chiqardi. */
                _jsxs("header", { className: "flex shrink-0 items-start justify-between gap-4 px-6 pt-6 pb-4", children: [_jsxs("div", { className: "flex min-w-0 flex-col gap-1", children: [_jsx("h2", { id: titleId, className: "text-xl font-semibold tracking-tight text-text", children: title }), description ? (_jsx("p", { id: descriptionId, className: "text-sm text-text-muted", children: description })) : null] }), _jsx("button", { type: "button", "data-dialog-close": "true", onClick: requestClose, disabled: busy, "aria-label": closeLabel, className: cn('-mr-1.5 -mt-1.5 flex size-9 shrink-0 items-center justify-center', 'rounded-[var(--radius-sm)] text-text-faint', 'transition-[background-color,color] duration-[var(--motion-fast)]', 'ease-[var(--ease-standard)] hover:bg-surface-2 hover:text-text', 'disabled:cursor-not-allowed disabled:opacity-50'), children: _jsx(X, { size: 20, "aria-hidden": true }) })] })), error ? (_jsxs("div", { role: "alert", className: cn('motion-field-error mx-6 mb-4 flex shrink-0 items-start gap-2.5', 'rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft', 'px-3.5 py-3 text-sm text-danger'), children: [_jsx(AlertTriangle, { size: 16, "aria-hidden": true, className: "mt-0.5 shrink-0" }), _jsx("span", { className: "min-w-0", children: error })] })) : null, _jsx("div", { className: cn(
                    /* AYLANADIGAN yagona qism. `min-h-0` bo'lmasa flex bolasining
                       eng kichik bo'yi kontentnikiga teng bo'lib qoladi va
                       `overflow-y-auto` hech qachon ishga tushmaydi — o'shanda
                       oyna cheklovni yorib, ekrandan chiqib ketardi. */
                    'min-h-0 flex-1 overflow-y-auto px-6 pb-6 text-base text-text', 
                    /* Aylanish oynada TUGAYDI, ortidagi sahifaga o'tmaydi.
                       Telefonda ro'yxat oxiriga yetgach barmoq harakati ostidagi
                       hujjatni surib yuborardi (`overflow: hidden` iOS da buni
                       to'xtata olmaydi). */
                    'overscroll-contain', 
                    // Sarlavha yo'q ekan, tepadagi bo'shliqni tana o'zi oladi.
                    headless ? 'pt-6' : undefined, bodyClassName), children: children }), footer ? (
                /*
                 * Telefonda tugmalar USTMA-UST va asosiysi TEPADA.
                 *
                 * Bitta qatorda ular barmoq uchun juda tor bo'lardi; tepaga
                 * chiqarilishi esa ataylab — bosh barmoq ekranning pastki
                 * qismiga yaqin turadi va u yerda "Bekor qilish" bo'lgani
                 * xavfsizroq: tasodifiy tegish ishni bekor qiladi, yo'q
                 * qilmaydi.
                 */
                _jsx("footer", { className: cn(
                    /* `shrink-0` — sarlavhadagi bilan bir sabab: uzun ro'yxatda
                       flex pastki panelni ham siqib, tugmalarni yassilab
                       qo'yardi. Ustidagi chegara esa panelning O'ZI aytadigan
                       xabar: kontent shu chiziq ostidan o'tib ketmoqda, ya'ni
                       pastda yana matn bor. */
                    'flex shrink-0 flex-col-reverse gap-2 bg-surface px-6 pt-4', 
                    /* `relative z-10` — SOYA uchun. Joylashuvsiz blokning
                       soyasi chizish tartibida MATNDAN oldin keladi va
                       ustidagi aylanadigan kontent uni yopib qo'yardi:
                       panel qog'ozga yopishib turgandek ko'rinardi. */
                    'relative z-10 border-t border-border shadow-[var(--shadow-sticky-top)]', 
                    /* Xavfsiz zona — telefonning pastki "chiziq" navigatsiyasi.
                       Usiz tugmalar aynan o'sha chiziq ostida qolib, bosib
                       bo'lmaydigan bo'lardi. */
                    'pb-[calc(1rem_+_env(safe-area-inset-bottom))] sm:pb-4', 'sm:flex-row sm:items-center sm:justify-end sm:gap-2', '[&>*]:w-full sm:[&>*]:w-auto', 
                    /* Telefonda har bir tugma kamida 44px — barmoq nishoni.
                       `min-h`, `h` emas: tugmaning o'z bo'yi (40px) o'rnida
                       qoladi va matn ikki qatorga o'ralsa quti o'sa oladi. */
                    'max-sm:[&>*]:min-h-[var(--size-control)]'), children: footer })) : null, askDiscard ? (
                /*
                 * Tasdiq oynaning O'ZIDA chiziladi, ikkinchi modal ochilmaydi.
                 *
                 * Ikkita modal ustma-ust tushganda fokus qopqoni ikkiga bo'linadi
                 * va `Esc` qaysi biriga tegishi noaniq bo'lib qoladi. Bu yerda esa
                 * qatlam bitta: ortidagi forma ko'rinib turadi va odam nimani
                 * yo'qotayotganini ko'radi.
                 */
                _jsx("div", { className: "motion-fade-in absolute inset-0 grid place-items-center bg-[var(--color-overlay)] p-6", children: _jsxs("div", { className: cn('motion-modal-in flex w-full max-w-[22rem] flex-col gap-4', 'rounded-[var(--radius-md)] bg-surface p-5 shadow-[var(--shadow-3)]'), children: [_jsx("p", { className: "text-base font-medium text-text", children: discardTitle }), _jsxs("div", { className: "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", children: [_jsx(Button, { variant: "ghost", onClick: () => setAskDiscard(false), children: discardCancelLabel }), _jsx(Button, { variant: "danger", onClick: () => {
                                            setAskDiscard(false);
                                            onClose();
                                        }, children: discardConfirmLabel })] })] }) })) : null] }) }), document.body);
};
//# sourceMappingURL=Dialog.js.map